import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "../../../utils/dayjs";
import Calendar from "../Calendar";
import getDateString from "../utils/getDateString";
const createTransform = (transform) => {
  return (target, min, max) => {
    return getDateString(transform(dayjs(target)).clamp(min, max, "date"));
  };
};
const enforceDateMinMax = createTransform((date) => date);
const startOfNextMonth = createTransform((date) => date.add(1, "month").startOf("month"));
const endOfPrevMonth = createTransform((date) => date.subtract(1, "month").endOf("month"));
const KEYBOARD_NAV_LOOKUP = {
  PageUp: endOfPrevMonth,
  PageDown: startOfNextMonth,
  ArrowLeft: createTransform((date) => date.subtract(1, "day")),
  ArrowRight: createTransform((date) => date.add(1, "day")),
  ArrowUp: createTransform((date) => date.subtract(1, "week")),
  ArrowDown: createTransform((date) => date.add(1, "week")),
  Home: createTransform((date) => date.startOf("week")),
  End: createTransform((date) => date.endOf("week"))
};
const DatePicker = ({
  intlMessages,
  locale,
  value,
  min,
  max,
  isDisabled,
  onChange,
  onTargetDateChange,
  renderCalendar: CalendarSlot = Calendar
}) => {
  const selectedValue = useMemo(() => getDateString(value), [value]);
  const [focusedValue, setFocusedValue] = useState(selectedValue);
  const cellLookup = useRef(/* @__PURE__ */ new Map());
  const targetDateRef = useRef(focusedValue);
  const setDateCellRef = useCallback((date, elem) => {
    cellLookup.current.set(getDateString(date), elem);
  }, []);
  const onKeyDown = useCallback((event) => {
    const { key } = event;
    if (event.key in KEYBOARD_NAV_LOOKUP) {
      event.stopPropagation();
      event.preventDefault();
      const transform = KEYBOARD_NAV_LOOKUP[key];
      setFocusedValue((prev) => transform(prev, min, max));
    }
  }, [min, max]);
  const onDateChange = useCallback((event, dateString) => {
    const nextAllowedDate = enforceDateMinMax(dateString, min, max);
    setFocusedValue(nextAllowedDate);
    onChange(event, nextAllowedDate);
  }, [max, min, onChange]);
  const onNextClick = useCallback(() => {
    setFocusedValue((prevFocusedValue) => {
      return startOfNextMonth(prevFocusedValue, min, max);
    });
  }, [max, min]);
  const onPrevClick = useCallback(() => {
    setFocusedValue((prevFocusedValue) => {
      return endOfPrevMonth(prevFocusedValue, min, max);
    });
  }, [max, min]);
  useEffect(() => {
    var _a;
    (_a = cellLookup.current.get(focusedValue)) == null ? void 0 : _a.focus();
    const prevDate = targetDateRef.current;
    const nextDate = dayjs(focusedValue).startOf("month").toString();
    onTargetDateChange == null ? void 0 : onTargetDateChange(prevDate, nextDate);
    targetDateRef.current = nextDate;
  }, [focusedValue, onTargetDateChange]);
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(CalendarSlot, {
    intlMessages,
    locale,
    min,
    max,
    selectedValue,
    focusedValue,
    onChange: onDateChange,
    onNextClick,
    onPrevClick,
    onKeyboardNavigation: onKeyDown,
    setDateCellRef,
    isDisabled
  }));
};
DatePicker.displayName = "Primitive(DateTimePicker)";
var DatePicker_default = DatePicker;
export {
  DatePicker_default as default
};
